<template>
  <div class="add-expense-wrapper">
    <!-- ===== HEADER ===== -->
    <div class="page-header">
      <div class="header-left">
        <h2>{{ form.expanse_id ? "Edit Expense" : "Add Expense" }}</h2>
        <p>Record a business expense for accurate financial tracking and reporting.</p>
      </div>
    </div>

    <!-- ===== LOADER ===== -->
    <div v-if="loading" class="loader-overlay">
      <div class="loader"></div>
      <p>Processing...</p>
    </div>

    <!-- ===== MAIN BODY ===== -->
    <div class="page-body">
      <!-- LEFT SIDE: MAIN FORM -->
      <div class="left-pane fade-in">
        <div class="card">
          <h3 class="section-title"><i class="bx bx-wallet"></i> Expense Details</h3>
          <p class="section-desc" style="margin-top: 1rem;">Provide expense information and assign it to categories or clients.</p>

          <div class="form-grid" style="margin-top: 1rem;">
            <div class="form-group">
              <label>Expense Title</label>
              <input type="text" v-model="form.expanse_title" placeholder="E.g. Office Rent, Software Subscription" />
            </div>

            <div class="form-group">
              <label>Expense Date</label>
              <input type="date" v-model="form.expanse_date" />
            </div>

            <div class="form-group">
              <label>Expense Category</label>
              <select v-model="form.expanse_category">
                <option value="">Select Category</option>
                <option value="Office Supplies">Office Supplies</option>
                <option value="Software">Software</option>
                <option value="Travel">Travel</option>
                <option value="Utilities">Utilities</option>
                <option value="Employee Salary">Employee Salary</option>
                <option value="Other">Other</option>

              </select>
            </div>

            <div class="form-group">
              <label>Amount ($)</label>
              <input type="number" v-model="form.expanse_amount" placeholder="Enter amount" />
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label>Payment Method</label>
              <select v-model="form.expanse_payment_mode">
                <option value="">Select Method</option>
                <option>Cash</option>
                <option>Bank Transfer</option>
                <option>Credit Card</option>
                <option>UPI / Wallet</option>
              </select>
            </div>

            <div class="form-group">
              <label>Vendor / Payee</label>
              <input type="text" v-model="form.expanse_vendor_name" placeholder="E.g. ABC Technologies" />
            </div>
          </div>

          <div class="form-group full">
            <label>Description</label>
            <textarea rows="3" v-model="form.expanse_notes" placeholder="Add any notes or expense details..."></textarea>
          </div>

          <div class="form-group full" style="margin-top: 1rem;">
            <label>Attach Receipt</label>
            <div class="upload-box" @click="triggerUpload">
              <input type="file" ref="fileInput" @change="handleFileChange" style="display: none" />
              <i class="bx bx-image-add"></i>
              <span>
                {{ form.expanse_receipt ? 'File Uploaded ✅' : 'Upload receipt image or PDF' }}
              </span>
            </div>
          </div>
        </div>

        <div class="form-footer">
          <button class="btn-secondary" @click="window.history.back()">
            <i class="bx bx-arrow-back"></i> Cancel
          </button>
          <button class="btn-primary" @click="saveExpense">
            <i class="bx bx-check"></i> Save Expense
          </button>
        </div>
      </div>

      <!-- RIGHT SIDE: TAX, PROJECT, AND SETTINGS -->
      <div class="right-pane slide-in">
        <div class="options-panel">
        

          <!-- PROJECT / CLIENT ASSIGNMENT -->
          <div class="option-card">
            <div class="option-header">
              <i class="bx bx-briefcase"></i>
              <h4>Project / Client</h4>
            </div>
            <p class="subtext">Assign this expense to a project or client for better tracking.</p>
            <div class="form-group">
              <label>Client</label>
              <select v-model="form.expanse_project_id">
                <option value="">Not Linked</option>
                <option v-for="client in clientList" :key="client.id" :value="client.id">
                  {{ client.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted } from "vue";
import { addExp,getExpById } from "@/apis/expapis";
import { getClients } from "@/apis/clientapis";
import { uploadFile } from "@/apis/invoiceapis";
import { useToast } from "vue-toastification";

const toast = useToast();

/* ==== STATE ==== */
const loading = ref(false);
const fileInput = ref(null);
const clientList = ref([]);
const form = ref({
  expanse_id: "",
  expanse_title: "",
  expanse_category: "",
  expanse_amount: "",
  expanse_date: "",
  expanse_payment_mode: "",
  expanse_vendor_name: "",
  expanse_notes: "",
  expanse_receipt: "",
  expanse_project_id: "",
  expanse_added_by: "",
  expanse_status: "active",
});

/* ==== FILE UPLOAD ==== */
const triggerUpload = () => fileInput.value.click();

const handleFileChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const formData = new FormData();
  formData.append("file", file);

  loading.value = true;
  const res = await uploadFile(formData);
  loading.value = false;

  if (res.status) {
    form.value.expanse_receipt = res.file_name || "";
    toast.success("Receipt uploaded successfully!");
  } else {
    toast.error(res.success || "File upload failed");
  }
};


/* ==== FETCH CLIENT LIST ==== */
const fetchClients = async () => {
  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  const res = await getClients(userData.user_id, "", 0, "");
  if (res.status && res.records) {
    clientList.value = res.records.map((c) => ({
      id: c.user_id,
      name: `${c.fname} ${c.lname}`.trim() || c.email,
    }));
  }
};

/* ==== SAVE EXPENSE ==== */
const saveExpense = async () => {
  if (!form.value.expanse_title) return toast.warning("Enter expense title");
  if (!form.value.expanse_amount) return toast.warning("Enter expense amount");

  const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};
  form.value.expanse_added_by = userData.user_id || "";

  loading.value = true;
  const res = await addExp(form.value);
  loading.value = false;

  if (res.status) {
    toast.success("Expense saved successfully");
    window.location.href = "/expanses";
  } else {
    toast.error(res.success || "Failed to save expense");
  }
};

const loadExpenseById = async (id) => {
  try {
    loading.value = true;
    const res = await getExpById(id);
    if (res.status && res.exp_dt) {
      form.value = {
        expanse_id: res.exp_dt.expanse_id,
        expanse_title: res.exp_dt.expanse_title || "",
        expanse_date: res.exp_dt.expanse_date || "",
        expanse_category: res.exp_dt.expanse_category || "",
        expanse_amount: res.exp_dt.expanse_total || "",
        expanse_payment_mode: res.exp_dt.expanse_payment_mode || "",
        expanse_vendor_name: res.exp_dt.expanse_vendor_name || "",
        expanse_notes: res.exp_dt.expanse_notes || "",
        expanse_receipt: res.exp_dt.expanse_receipt || "",
        expanse_project_id: res.exp_dt.expanse_project_id || "",
      };
    } else {
      toast.warning("Expense not found");
    }
  } catch (err) {
    console.error("loadExpenseById error:", err);
  } finally {
    loading.value = false;
  }
};


/* ==== LIFECYCLE ==== */
onMounted(async() => {
  fetchClients();
  const params = new URLSearchParams(window.location.search);
  const id = params.get("id");
  if (id) {
    await loadExpenseById(id);
  } else {
    // Default new expense date
    form.value.expanse_date = new Date().toISOString().slice(0, 10);
  }
});

</script>


<style scoped>
.add-expense-wrapper {
  background: #fff;
  border-radius: 1rem;
  padding: 2.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  font-family: "Inter", sans-serif;
  animation: fadeIn 0.5s ease;
}

/* HEADER */
.page-header {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 2.5rem;
}
.header-left h2 {
  font-size: 1.6rem;
  color: #111827;
  font-weight: 700;
}
.header-left p {
  color: #6b7280;
  font-size: 0.95rem;
}
.header-right {
  display: flex;
  gap: 0.8rem;
}

/* BUTTONS */
.btn-primary,
.btn-secondary {
  border: none;
  padding: 0.8rem 1.6rem;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.3s;
}
.btn-primary {
  background: #111827;
  color: #fff;
}
.btn-primary:hover {
  background: #1f2937;
}
.btn-secondary {
  background: #f3f4f6;
  color: #111827;
}
.btn-secondary:hover {
  background: #e5e7eb;
}

/* BODY */
.page-body {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
}
.left-pane {
  width: 60%;
}
.right-pane {
  width: 40%;
  overflow: visible;
}

/* CARD */
.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

/* FORM GRID */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem 2rem;
  margin-bottom: 1rem;
}
.form-group {
  display: flex;
  flex-direction: column;
}
label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
}
input,
select,
textarea {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.8rem;
  font-size: 0.95rem;
  transition: border 0.3s ease;
}
input:focus,
select:focus,
textarea:focus {
  border-color: #111827;
  outline: none;
}

/* RECEIPT UPLOAD */
.upload-box {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  text-align: center;
  padding: 1.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
}
.upload-box:hover {
  border-color: #111827;
  color: #111827;
}

/* RIGHT SIDE OPTIONS */
.options-panel {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.option-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
}
.option-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}
.option-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #111827;
  margin-bottom: 0.4rem;
}
.subtext {
  color: #6b7280;
  font-size: 0.85rem;
  margin-bottom: 0.8rem;
}

/* TAX LIST */
.tax-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.tax-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.9rem 1rem;
}
.tax-inputs {
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
}
.tax-inputs input,
.tax-inputs select {
  flex: 1;
}
.btn-remove {
  background: transparent;
  border: none;
  color: #dc2626;
  cursor: pointer;
  margin-top: 0.6rem;
}
.btn-outline {
  width: 100%;
  border: 1px dashed #9ca3af;
  background: #fff;
  padding: 0.7rem;
  border-radius: 8px;
  color: #111827;
  font-weight: 500;
  cursor: pointer;
}
.btn-outline:hover {
  background: #f3f4f6;
}

/* SWITCH */
.switch-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
}
.switch {
  position: relative;
  width: 46px;
  height: 24px;
}
.switch input {
  opacity: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background-color: #d1d5db;
  border-radius: 24px;
  transition: 0.4s;
}
.slider:before {
  content: "";
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.4s;
}
input:checked + .slider {
  background-color: #111827;
}
input:checked + .slider:before {
  transform: translateX(22px);
}

/* FOOTER */
.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

/* ANIMATIONS */
.fade-in {
  animation: fadeIn 0.5s ease;
}
.slide-in {
  animation: slideIn 0.5s ease;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(8px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 992px) {
  .page-body {
    flex-direction: column;
  }
  .left-pane,
  .right-pane {
    width: 100%;
  }
}
/* === 📱 MOBILE RESPONSIVE PATCH === */
@media (max-width: 992px) {
  .add-expense-wrapper {
    padding: 1.8rem !important;
  }

  .page-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 1rem !important;
  }

  .header-left h2 {
    font-size: 1.4rem !important;
  }

  .header-left p {
    font-size: 0.9rem !important;
  }

  .header-right {
    width: 100% !important;
    justify-content: flex-start !important;
    gap: 0.6rem !important;
  }

  .btn-primary,
  .btn-secondary {
    font-size: 0.9rem !important;
    padding: 0.7rem 1.2rem !important;
  }

  .page-body {
    flex-direction: column !important;
    gap: 1.5rem !important;
  }

  .left-pane,
  .right-pane {
    width: 100% !important;
  }

  .form-grid {
    grid-template-columns: 1fr !important;
    gap: 1rem !important;
  }

  .card {
    padding: 1.5rem !important;
  }

  .upload-box {
    padding: 1.4rem !important;
    font-size: 0.9rem !important;
  }

  .upload-box i {
    font-size: 1.6rem !important;
  }

  .form-footer {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 0.8rem !important;
  }

  .form-footer button {
    width: 100% !important;
    font-size: 0.95rem !important;
  }

  .option-card {
    padding: 1.2rem !important;
  }

  .tax-inputs {
    flex-direction: column !important;
    gap: 0.6rem !important;
  }

  .tax-inputs input,
  .tax-inputs select {
    width: 100% !important;
  }

  .btn-outline {
    font-size: 0.9rem !important;
    padding: 0.6rem !important;
  }
}

@media (max-width: 600px) {
  .add-expense-wrapper {
    padding: 1.2rem !important;
    border-radius: 0.8rem !important;
  }

  .header-left h2 {
    font-size: 1.25rem !important;
  }

  .btn-primary,
  .btn-secondary {
    width: 100% !important;
    text-align: center !important;
  }

  .form-grid {
    grid-template-columns: 1fr !important;
    gap: 0.8rem !important;
  }

  .form-group label {
    font-size: 0.85rem !important;
  }

  .option-card h4 {
    font-size: 1rem !important;
  }

  .subtext {
    font-size: 0.8rem !important;
  }

  .upload-box {
    padding: 1rem !important;
  }

  .tax-box {
    padding: 0.8rem 1rem !important;
  }
}

@media (max-width: 420px) {
  .page-header {
    gap: 0.8rem !important;
  }

  .header-left h2 {
    font-size: 1.1rem !important;
  }

  .header-left p {
    font-size: 0.8rem !important;
  }

  .form-footer button {
    font-size: 0.85rem !important;
    padding: 0.6rem 1rem !important;
  }

  .btn-outline {
    font-size: 0.85rem !important;
  }
}
/* === 🌀 LOADER OVERLAY === */
.loader-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.4s ease forwards;
}

.loader-overlay p {
  margin-top: 1rem;
  color: #374151;
  font-size: 0.95rem;
  font-weight: 500;
}

/* === SPINNER === */
.loader {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #111827;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* === EMPTY UPLOAD PREVIEW (Better visual feedback) === */
.upload-box span {
  display: block;
  margin-top: 0.6rem;
  font-size: 0.9rem;
  color: #6b7280;
  transition: color 0.3s ease;
}
.upload-box:hover span {
  color: #111827;
}

/* === BUTTON STATES (Hover lift) === */
.btn-primary:active, .btn-secondary:active {
  transform: scale(0.97);
  transition: transform 0.1s ease-in;
}

/* === SUBTLE INPUT SHADOW ON FOCUS === */
input:focus, select:focus, textarea:focus {
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.1);
}

/* === TAX SECTION PATCH === */
.tax-box:hover {
  border-color: #111827;
  transition: all 0.3s ease;
}

/* === RESPONSIVE FIXES === */
@media (max-width: 600px) {
  .loader {
    width: 40px;
    height: 40px;
    border-width: 3px;
  }
  .loader-overlay p {
    font-size: 0.85rem;
  }
}

</style>
